import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState, useRef, useMemo} from 'react';
import gsap from 'gsap';

//* HOO'C
import { withLanguageContext, withUIContext } from '@/context';

//* Style
import HeroBuildingStyle from './style';

//* Component
import NavigationBuildingHero from './Navigation';
import Text from '@/components/common/Text';
import InfoBuilding from './InfoBuilding';

const HeroBuilding = ({ name, slug, translate, selectedLang, floorData, dataReady, isTouch, winWidth }) => {
	//! State
	const [data, setData] = useState(false);
	const [floors, setFloors] = useState([]);
	const [updateFloor, setUpdateFloor] = useState(false);

	//! Ref
	const wrapperAll = useRef();
	const objectSvgContainer = useRef();
	const pressTimer = useRef();
	const isLongPress = useRef(false);
	const moved = useRef(false);

	//! Router
	const router = useRouter();
	const pathname = usePathname();

	//! Mobile check
	const isMobileSize = useMemo(() => winWidth < 768, [winWidth]);

	//! Get Svg
	useEffect(() => {
		async function insertSVG() {
			try {
				const response = await fetch(`/images/building/${name}.svg`);
				const svgText = await response.text();
				setData(svgText);
			} catch (error) {
				console.error(error);
			}
		}
		insertSVG();
	}, [name]);

	//! Resize observer
	useEffect(() => {
		const observer = new ResizeObserver(() => {
			if (objectSvgContainer.current && data) {
				recalculateFloors();
			}
		});

		if (objectSvgContainer.current) {
			observer.observe(objectSvgContainer.current);
		}

		return () => {
			observer.disconnect();
		};
	}, [data]);

	//! Function to recalculate floor coordinates
	const recalculateFloors = () => {
		const svgElement = objectSvgContainer.current.querySelector('svg');
		const updatedFloors = [];

		if (svgElement) {
			const layers = [...svgElement.querySelector('image').parentElement.children].slice(2);

			layers.forEach((item, i) => {
				if (item.tagName == 'g') {
					item.classList.add('layer-item');

					if (floorData.length) {
						if (floorData[i]) {
							// item.classList.add('available');

							const bbox = item.getBBox();
							const ctm = item.getScreenCTM();
							const adjustedPoint = svgElement.createSVGPoint();
							adjustedPoint.x = bbox.x;
							adjustedPoint.y = bbox.y;
							const transformedPoint = adjustedPoint.matrixTransform(ctm);
							const scaleX = ctm.a;
							const scaleY = ctm.d;
							const adjustedWidth = bbox.width * scaleX;
							const adjustedHeight = bbox.height * scaleY;

							updatedFloors.push(createFloorInfo(i, Math.floor(transformedPoint.x), Math.floor(transformedPoint.y), Math.floor(adjustedWidth), Math.floor(adjustedHeight)));
						}
					}
				}
			});
		}

		setFloors(updatedFloors);
		setUpdateFloor(true);
	};

	//! Create floor info
	function createFloorInfo(i, x, y, width, height) {
		return (
			<div
				className={`every-item-floor item-floor-${i + 1}`}
				style={{
					position: 'absolute',
					left: x,
					top: y,
					width: width,
					height: height,
				}}>
				<div className='text-info-wrapper'>
					<Text className={'font-montserrat-semibold text-info h5'}>{i + 2}</Text>
					{floorData.length ? (isMobileSize ? (
						<div className="wrapper-select-floor-to-view">
							<Text className={'font-montserrat-medium h6'}>
								Select a Floor to View
							</Text>
						</div>
					) : (
						<div className='wrapper-info-individual-floor'>
							<Text className={'font-montserrat-medium p1'}>
								{translate('floor')}:&nbsp;{i + 2}
							</Text>
							{floorData[floorData.length - i - 1] && (
								<Text className={'font-montserrat-medium p1'}>
									{translate('available')}:&nbsp;{floorData[floorData.length - i - 1].available_units}&nbsp;
									{translate('units')}
								</Text>
							)}
							{floorData[floorData.length - i - 1] && (
								<Text className={'font-montserrat-medium p1'}>
									{translate('totalSpace')}:&nbsp;{floorData[floorData.length - i - 1].available_units + floorData[floorData.length - i - 1].reserved_units + floorData[floorData.length - i - 1].sold_units}&nbsp;
									{translate('units')}
								</Text>
							)}
						</div>
					)) : null}
				</div>
			</div>
		);
	}

	//! Get parser svg
	useEffect(() => {
		if (data && objectSvgContainer.current) {
			const parser = new DOMParser();
			const svgElement = parser.parseFromString(data, 'image/svg+xml').documentElement;

			objectSvgContainer.current.innerHTML = '';
			objectSvgContainer.current.appendChild(svgElement);

			gsap.fromTo(svgElement, { opacity: 0 }, { opacity: 1, ease: 'power2.out', duration: 0.6 });
			recalculateFloors();
		}
	}, [data]);

	useEffect(() => {
		if (objectSvgContainer.current) {
			const svgElement = objectSvgContainer.current.querySelector('svg');
			if (svgElement) {
				const layers = [...svgElement.querySelector('image').parentElement.children].slice(2);

				layers.map((item, i) => {
					if (floorData.length) {
						const itemBlock = wrapperAll.current.querySelector(`.item-floor-${i + 1}`);
						if (floorData.length > 7) {
							if (i > 7) {
								if (itemBlock) itemBlock.classList.add('item-reverse');
							}
						} else {
							if (i > 3) {
								if (itemBlock) itemBlock.classList.add('item-reverse');
							}
						}

						if (Math.round(layers.length / 2) === i) {
							item.classList.add('available');

							if (itemBlock) {
								itemBlock.classList.add('active');
							}
						}

						if (floorData[i]) {
							if (item.tagName == 'g') {
								if (!isMobileSize) {
									item.addEventListener('click', () => {
										router.push(`/${selectedLang}/buildings/${slug}/floor${i + 2}`);
									});
									item.addEventListener('mousemove', () => {
										if (wrapperAll.current) {
											item.classList.add('available');

											const itemBlock = wrapperAll.current.querySelector(`.item-floor-${i + 1}`);
											if (itemBlock) {
												itemBlock.classList.add('active');
											}
										}
									});
									item.addEventListener('mouseleave', () => {
										if (wrapperAll.current) {
											item.classList.remove('available');

											const itemBlock = wrapperAll.current.querySelector(`.item-floor-${i + 1}`);
											if (itemBlock) {
												itemBlock.classList.remove('active');
											}
										}
									});
								}
								if (isTouch) {
									item.addEventListener('touchstart', (ev) => {
										isLongPress.current = false;
										moved.current = false;

										layers.map((item, i) => {
											item.classList.remove('available');

											const itemBlock = wrapperAll.current.querySelector(`.item-floor-${i + 1}`);
											if (itemBlock) {
												itemBlock.classList.remove('active');
											}
										});

										pressTimer.current = setTimeout(() => {
											isLongPress.current = true;
											item.classList.add('available');

											const itemBlock = wrapperAll.current.querySelector(`.item-floor-${i + 1}`);
											if (itemBlock) {
												itemBlock.classList.add('active');
											}
										}, 100);
									});

									item.addEventListener('touchmove', (e) => {
										moved.current = true;
										clearTimeout(pressTimer.current);
									});

									item.addEventListener('touchend', (e) => {
										clearTimeout(pressTimer.current);

										if (!isLongPress.current && !moved.current) {
											router.push(`/${selectedLang}/buildings/${slug}/floor${i + 2}`);
										}
									});
								}
							}
						}
					}
				});

				// if (isTouch) {
				// 	const handleOutsideClick = (e) => {
				// 		if (svgElement && !svgElement.contains(e.target)) {
				// 			layers.map((item, i) => {
				// 				item.classList.remove('available');
				// 				if (wrapperAll.current) {
				// 					const itemBlock = wrapperAll.current.querySelector(`.item-floor-${i + 1}`);
				// 					if (itemBlock) {
				// 						itemBlock.classList.remove('active');
				// 					}
				// 				}
				// 			});
				// 		}
				// 	};
				//
				// 	document.addEventListener('touchstart', handleOutsideClick);
				// }
			}
		}
	}, [updateFloor]);

	return (
		<HeroBuildingStyle ref={wrapperAll} className={`building-${slug}`}>
			<div
				ref={objectSvgContainer}
				className='wrapper-svg'
			/>
			{updateFloor && floors.length > 0
				? floors.map((item, i) => (
						<div
							className={`every-item-floor-wrapper`}
							key={i}>
							{item}
						</div>
				  ))
				: null}

			{data ? (
				<>
					<div className='wrapper-navigation'>
						<NavigationBuildingHero
							slug={slug}
							nameActive={name}
						/>
					</div>
					<div className='wrapper-info-building'>
						<InfoBuilding
							floorData={floorData}
							dataReady={dataReady}
							name={name}
						/>
					</div>
				</>
			) : null}
		</HeroBuildingStyle>
	);
};

export default withUIContext(withLanguageContext(HeroBuilding, ['translate', 'selectedLang']), ['isTouch', 'winWidth']);
